// Photo helpers for "moment" waypoints — a port of Flutter's
// photo_map_box_marker.dart `imageUrlsFor` and the carousel in
// waypoint_tooltip_popup.dart (`_WaypointTooltipPhotoCarousel`).

/** Flutter's photo accent (waypoint_visual.dart Photo case). */
export const MOMENT_ACCENT = '#0057E3';

const MOMENT_TYPES = new Set(['moment', 'photo']);

export function isMomentType(type: string): boolean {
  return MOMENT_TYPES.has(String(type || '').trim().toLowerCase());
}

/** Flutter TextPost.isFeedOnly: a written post is not a place — feed only, never on the map. */
export function isFeedOnlyWaypoint(wp: any): boolean {
  return String(wp?.type || wp?.waypoint_type || '').trim().toLowerCase() === 'post';
}

function urlFrom(entry: unknown): string {
  if (typeof entry === 'string') return entry.trim();
  if (entry && typeof entry === 'object') {
    const e = entry as Record<string, unknown>;
    const v = e['image_url'] ?? e['url'] ?? e['imageUrl'];
    return typeof v === 'string' ? v.trim() : '';
  }
  return '';
}

/**
 * Every image URL of a waypoint, deduped, in Flutter's priority order:
 * signed `image_urls`, `images[].image_url`, then the `details` fallbacks.
 */
export function getWaypointImageUrls(wp: any): string[] {
  const out: string[] = [];
  const push = (entry: unknown) => {
    const url = urlFrom(entry);
    if (url && !out.includes(url)) out.push(url);
  };
  const pushAll = (list: unknown) => {
    if (Array.isArray(list)) list.forEach(push);
  };

  pushAll(wp?.image_urls);
  pushAll(wp?.images);
  const details = wp?.details;
  if (details && typeof details === 'object') {
    pushAll(details.image_urls);
    pushAll(details.images);
    pushAll(details.waypoint_images);
    push(details.image_url);
  }
  return out;
}

/** Attribute-safe URL (signed URLs carry `&`). */
function attr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/** Map thumbnail pin: 44px photo face + tail, count pill for multi-photo moments (Flutter PhotoMapBoxMarker). */
export function buildMomentPinHtml(coverUrl: string, photoCount: number): string {
  const badge = photoCount > 1 ? `<span class="moment-pin__count">${photoCount}</span>` : '';
  return `<div class="moment-pin"><div class="moment-pin__face"><img class="main-marker-image" src="${attr(coverUrl)}" alt="" loading="lazy"></div><i class="moment-pin__tail"></i>${badge}</div>`;
}

/** 4:3 edge-to-edge carousel; nav + counter are only rendered for 2+ photos. */
export function buildWaypointPhotoCarouselHtml(urls: string[]): string {
  if (!urls.length) return '';
  const slides = urls
    .map((u) => `<div class="wp-carousel__slide"><img src="${attr(u)}" alt="" loading="lazy" data-action="photo-open" data-url="${attr(u)}"></div>`)
    .join('');
  const multi = urls.length > 1;
  const dots = multi
    ? `<div class="wp-carousel__dots">${urls.map((_, i) => `<span class="wp-carousel__dot${i === 0 ? ' is-active' : ''}"></span>`).join('')}</div>`
    : '';
  const counter = multi ? `<span class="wp-carousel__count">1/${urls.length}</span>` : '';
  const nav = multi
    ? `<button class="wp-carousel__nav wp-carousel__nav--prev" data-action="photo-prev" aria-label="Previous photo">‹</button>
       <button class="wp-carousel__nav wp-carousel__nav--next" data-action="photo-next" aria-label="Next photo">›</button>`
    : '';
  return `<div class="wp-carousel"><div class="wp-carousel__track">${slides}</div>${counter}${dots}${nav}</div>`;
}

/** Wires prev/next + counter/dot sync on a mounted popup. Safe if there is no carousel. */
export function bindWaypointPhotoCarousel(root: HTMLElement): void {
  const track = root.querySelector('.wp-carousel__track') as HTMLElement | null;
  if (!track) return;
  const count = root.querySelector('.wp-carousel__count') as HTMLElement | null;
  const dots = Array.from(root.querySelectorAll('.wp-carousel__dot'));
  const total = track.children.length;

  const sync = () => {
    const index = Math.min(total - 1, Math.max(0, Math.round(track.scrollLeft / track.clientWidth)));
    if (count) count.textContent = `${index + 1}/${total}`;
    dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
  };
  track.addEventListener('scroll', sync, { passive: true });

  root.addEventListener('click', (e) => {
    const action = ((e.target as HTMLElement).closest('[data-action]') as HTMLElement | null)?.dataset['action'];
    if (action === 'photo-prev' || action === 'photo-next') {
      e.stopPropagation();
      track.scrollBy({ left: (action === 'photo-next' ? 1 : -1) * track.clientWidth, behavior: 'smooth' });
    }
  });
}
